import { get, post, put } from "aws-amplify/api";
import { default as React, useState, useEffect, useCallback, useMemo } from "react";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import { PacmanLoader } from "react-spinners";
import { Alert, Button, Col, Row } from "react-bootstrap";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import "containers/common/List.css";
import "containers/common/Table.css";
import { PAGE_SIZES } from "utils/pageSizeConfig";

import {
  createColumnHelper,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

function CPIRatesList({ isAuthenticated, user }) {
  const cpiRatesIdPrefix = "cpi-rates-list";

  const [isLoading, setIsLoading] = useState(true);
  const [ui, setUi] = useState({ showAlert: false, errorMessage: "" });
  const [sorting, setSorting] = useState([]);
  const [data, setData] = useState([]);

  // --- API Functions ---
  const getData = useCallback(async () => {
    const response = await get({
      apiName: "colin",
      path: "/cpirates",
    }).response;
    try {
      const cs = await response.body.json();
      return cs;
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    if (!isAuthenticated) {
      return;
    }

    (async () => {
      try {
        const localcpirates = await getData();
        setData(localcpirates || []);
      } catch (e) {
        alert(e);
        setUi({ showAlert: true, errorMessage: "Failed to load data." });
      }
      setIsLoading(false);
    })();
  }, [isAuthenticated, getData]);

  const saveRecord = async (record) => {
    const isNew = record.new_record;
    const path = isNew ? "/cpirates/new" : `/cpirates/${record["cpi_id"]}`;
    const method = isNew ? post : put;

    try {
      const result = await method({
        apiName: "colin",
        path: path,
        options: { body: { ...record, last_edited_by: user } }
      });
      const response = await result.response;
      return await response.body.json();
    } catch (err) {
      console.error("Save error:", err);
      throw err;
    }
  };

  // --- Table Logic ---
  const updateTableState = (rowIndex, columnId, value) => {
    setData(old => old.map((record, index) => (
      index === rowIndex
        ? { ...record, [columnId]: value, modified: true }
        : record
    )));
  };

  // --- Save Logic Functions ---
 const handleSave = async ({ row, column, value }) => {
     const rowId = row.id;
    const recordToSave = { ...row.original, [column.id]: value };

    // Validation
    if (column.id === "year" && !row.original.new_record) {
      alert("Cannot change existing Year. Record not saved.");
      setData(old => old.map((record, index) =>
        index === row.index ? { ...record, year: row.original.year } : record
      ));
      return false;
    }

    const numericColumns = [
      "year",
      "march_quarter",
      "june_quarter",
      "september_quarter",
      "december_quarter",
    ];

    if (numericColumns.includes(column.id)) {
      const rawValue = value == null ? "" : String(value).trim();
      const columnLabel = column.id
        .split("_")
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");

      if (column.id === "year") {
        if (recordToSave.new_record && rawValue === "") return;

        const isInteger = /^-?\d+$/.test(rawValue);
        if (!isInteger) {
          setUi({
            showAlert: true,
            errorMessage: `${columnLabel} must be numeric`,
          });
          return false;
        }

        recordToSave.year = Number(rawValue);
      } else {
        if (rawValue === "") {
          recordToSave[column.id] = null;
        } else {
          const isNumeric = /^-?(\d+|\d*\.\d+)$/.test(rawValue);
          if (!isNumeric) {
            setUi({
              showAlert: true,
              errorMessage: `${columnLabel} must be numeric`,
            });
            return false;
          }

          recordToSave[column.id] = Number(rawValue);
        }
      }
    }

    if (recordToSave.new_record && !recordToSave.year) return;

    try {
      const response = await saveRecord(recordToSave);

      if (response && response.result === false) {
        setUi({
          showAlert: true,
          errorMessage: response.error_message || "Failed to save record.",
        });
        return false;
      }

      const newId = response?.id || response?.cpi_id;
      const isCreate = Boolean(recordToSave.new_record);

      setData(old => old.map((r, idx) =>
        String(idx) === rowId
          ? {
              ...r,
              [column.id]: recordToSave[column.id],
              cpi_id: isCreate ? (newId ?? r.cpi_id) : r.cpi_id,
              modified: false,
              new_record: false,
            }
          : r
      ));
      return true;
    } catch (err) {
      setUi({ showAlert: true, errorMessage: "Network Error: Could not save record." });
      return false;
    }
  };

  const addRecord = () => {
    setData(prev => [{
      cpi_id: "",
      year: "",
      march_quarter: null,
      june_quarter: null,
      september_quarter: null,
      december_quarter: null,
      created_by: user,
      last_edited_by: user,
      new_record: true,
    }, ...prev]);
  };

  // --- Column Definitions ---
  const columns = useMemo(() => [
    columnHelper.accessor("cpi_id", {
      id: "cpi_id",
      header: "ID",
      cell: (info) => info.getValue() || "",
      sortingFn: (rowA, rowB, columnId) => {
        const a = Number(rowA.getValue(columnId));
        const b = Number(rowB.getValue(columnId));

        if (Number.isNaN(a) && Number.isNaN(b)) return 0;
        if (Number.isNaN(a)) return 1;
        if (Number.isNaN(b)) return -1;
        return a - b;
      },
      size: 150,
      enableSorting: true,
    }),
    columnHelper.accessor("year", {
      id: "year",
      header: "Year",
      cell: EditableCell,
      size: 350,
      enableSorting: false,
    }),
    columnHelper.accessor("march_quarter", {
      id: "march_quarter",
      header: "March Quarter",
      cell: EditableCell,
      size: 350,
      enableSorting: false,
    }),
    columnHelper.accessor("june_quarter", {
      id: "june_quarter",
      header: "June Quarter",
      cell: EditableCell,
      size: 350,
      enableSorting: false,
    }),
    columnHelper.accessor("september_quarter", {
      id: "september_quarter",
      header: "September Quarter",
      cell: EditableCell,
      size: 350,
      enableSorting: false,
    }),
    columnHelper.accessor("december_quarter", {
      id: "december_quarter",
      header: "December Quarter",
      cell: EditableCell,
      size: 350,
      enableSorting: false,
    }),
  ], []);

  const createId = useMemo(
    () => createElementIdFactory(cpiRatesIdPrefix),
    [cpiRatesIdPrefix]
  );

  const tableElementIds = useMemo(
    () => createReusableTableElementIds(createId),
    [createId]
  );

  if (!isAuthenticated) return null;

  // --- Render Functions ---
  return (
    <div className="mt-3">
      <Alert show={ui.showAlert} variant="danger" onClose={() => setUi({ ...ui, showAlert: false })} dismissible>
        {ui.errorMessage}
      </Alert>

      <Col className="text-right" style={{ marginBottom: '12px' }}>
        <Button id={createId("add", "button")} variant="secondary" size="sm" onClick={addRecord}>Add</Button>
      </Col>

      <Row>
        <Col>
          {isLoading ? (
            <div className="outer-div">
              <div className="sweet-loading inner-div">
                <PacmanLoader
                  cssOverride={{ display: "block", margin: "0 auto" }}
                  size={20}
                  color={"#e03c31"}
                  loading={isLoading}
                />
              </div>
            </div>
          ) : (
            <div className="custom-data-table">
              <GenericTable
                columns={columns}
                data={data}
                section="cpi-rates-list"
                elementIds={tableElementIds}
                pageSize={PAGE_SIZES.cpiRates}
                enablePagination={true}
                state={{ sorting }}
                onSortingChange={setSorting}
                enableSortingRemoval={false}
                meta={{
                  updateData: updateTableState,
                  handleSave: handleSave,
                }}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}

export default CPIRatesList;